import { IoPersonOutline, IoPeopleOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { hasSimplifiedSetup } from '../../utils/userSetup';
import './ReaderSelector.css';

const ReaderSelector = ({
  readers,
  families,
  readerMode,
  selectedReaders,
  selectedFamily,
  onReaderModeChange,
  onFamilySelect,
  onReaderToggle,
  showAllOption = false
}) => {
  const primaryReader = readers.find(r => r.isPrimary);
  const isSimplifiedSetup = hasSimplifiedSetup(readers, families);

  return (
    <div className="reader-selector">
      {/* Mode Selection */}
      <div className="reader-mode-tabs">
        {showAllOption && !isSimplifiedSetup && (
          <button
            type="button"
            className={`mode-tab ${readerMode === 'all' ? 'active' : ''}`}
            onClick={() => onReaderModeChange('all')}
          >
            <IoPeopleOutline size={20} />
            <span>All readers</span>
            <small>Show every reader</small>
          </button>
        )}
        
        <button
          type="button"
          className={`mode-tab ${readerMode === 'primary' ? 'active' : ''}`}
          onClick={() => onReaderModeChange('primary')}
          disabled={!primaryReader}
        >
          <IoPersonOutline size={20} />
          <span>Just me</span>
          {primaryReader && <small>({primaryReader.name})</small>}
        </button>
        
        {!isSimplifiedSetup && (
          <>
            <button
              type="button"
              className={`mode-tab ${readerMode === 'family' ? 'active' : ''}`}
              onClick={() => onReaderModeChange('family')}
              disabled={families.length === 0}
            >
              <IoPeopleOutline size={20} />
              <span>Whole family</span>
              {families.length > 0 && <small>({families.length} families)</small>}
            </button>
            
            <button
              type="button"
              className={`mode-tab ${readerMode === 'specific' ? 'active' : ''}`}
              onClick={() => onReaderModeChange('specific')}
              disabled={readers.length <= 1}
            >
              <IoPersonOutline size={20} />
              <span>Specific readers</span>
              <small>Choose individuals</small>
            </button>
          </>
        )}
      </div>

      {/* Primary Reader Mode */}
      {readerMode === 'primary' && primaryReader && (
        <div className="reader-display">
          <div className="selected-reader primary">
            <IoPersonOutline className="reader-icon" />
            <div className="reader-info">
              <span className="reader-name">{primaryReader.name}</span>
              <small className="reader-type">Primary Reader</small>
            </div>
            <IoCheckmarkCircleOutline className="selected-icon" />
          </div>
        </div>
      )}

      {/* Family Mode */}
      {readerMode === 'family' && (
        <div className="family-selector">
          {families.length === 0 ? (
            <div className="no-families">
              <p>You haven't joined any families yet.</p>
              <small>Visit the Family page to create or join a family.</small>
            </div>
          ) : (
            <div className="family-list">
              {families.map(family => (
                <div
                  key={family.id}
                  className={`family-option ${selectedFamily?.id === family.id ? 'selected' : ''}`}
                  onClick={() => onFamilySelect(family)}
                >
                  <div className="family-header">
                    <IoPeopleOutline className="family-icon" />
                    <div className="family-info">
                      <span className="family-name">{family.name}</span>
                      <small className="family-members">
                        {family.readers?.length || 0} readers
                      </small>
                    </div>
                    {selectedFamily?.id === family.id && (
                      <IoCheckmarkCircleOutline className="selected-icon" />
                    )}
                  </div>
                  
                  {selectedFamily?.id === family.id && family.readers && (
                    <div className="family-readers">
                      {family.readers.map(reader => (
                        <div key={reader.id} className="family-reader">
                          <IoPersonOutline size={16} />
                          <span>{reader.name}</span>
                          {reader.isPrimary && <small>(Primary)</small>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Specific Readers Mode */}
      {readerMode === 'specific' && (
        <div className="specific-readers">
          {readers.length <= 1 ? (
            <div className="no-readers">
              <p>You need multiple readers to use this option.</p>
              <small>Add more readers from Family → Readers.</small>
            </div>
          ) : (
            <div className="reader-list">
              {readers.map(reader => (
                <div
                  key={reader.id}
                  className={`reader-option ${selectedReaders.some(r => r.id === reader.id) ? 'selected' : ''}`}
                  onClick={() => onReaderToggle(reader)}
                >
                  <IoPersonOutline className="reader-icon" />
                  <div className="reader-info">
                    <span className="reader-name">{reader.name}</span>
                    {reader.isPrimary && <small className="reader-type">Primary</small>}
                  </div>
                  {selectedReaders.some(r => r.id === reader.id) && (
                    <IoCheckmarkCircleOutline className="selected-icon" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ReaderSelector;
